import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const ServicesMarquee = () => {
  const navigate = useNavigate();
  const services = [
    { name: 'Website Development', icon: '🌐', gradient: 'from-primary-500 to-secondary-500' },
    { name: 'Web Hosting', icon: '☁️', gradient: 'from-accent-cyan to-primary-400' },
    { name: 'Logo Design', icon: '🎨', gradient: 'from-secondary-500 to-accent-rose' },
    { name: 'SEO Optimization', icon: '📈', gradient: 'from-accent-emerald to-primary-500' },
    { name: 'Portfolio Creation', icon: '💼', gradient: 'from-primary-600 to-accent-indigo' },
    { name: 'Flyer Design', icon: '📄', gradient: 'from-accent-amber to-accent-rose' },
    { name: 'Digital Marketing', icon: '📱', gradient: 'from-accent-cyan to-secondary-500' },
    { name: 'Brand Identity', icon: '✨', gradient: 'from-secondary-600 to-primary-500' },
    { name: 'E-commerce', icon: '🛒', gradient: 'from-accent-emerald to-accent-cyan' },
    { name: 'UI/UX Design', icon: '🎯', gradient: 'from-primary-500 to-accent-indigo' }
  ];

  // Create seamless loop with enough duplicates
  const extendedServices = [...services, ...services, ...services];

  return (
    <section className="relative py-8 sm:py-16 overflow-hidden bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-neutral-900 dark:to-secondary-900/20">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-gradient-to-br from-accent-cyan to-primary-400 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-gradient-to-br from-secondary-400 to-accent-rose rounded-full blur-2xl"></div>
      </div>

      {/* Section Header */}
      <div className="container mx-auto px-4 mb-8 sm:mb-12 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gradient-primary mb-2 sm:mb-4">
            Our Services
          </h2>
          <p className="text-sm sm:text-base text-text-secondary dark:text-text-dark-secondary max-w-2xl mx-auto">
            Discover the comprehensive range of digital solutions we offer to transform your business
          </p>
        </motion.div>
      </div>

      {/* Diagonal Marquee Container */}
      <div className="relative space-y-4 sm:space-y-8">
        {/* First diagonal marquee - moving right */}
        <div className="transform rotate-1 sm:rotate-3 overflow-hidden">
          <div className="relative py-2 sm:py-4">
            <div className="flex animate-marquee-right gap-3 sm:gap-6 whitespace-nowrap">
              {extendedServices.map((service, index) => (
                <motion.div
                  key={`top-${index}`}
                  className={`flex-shrink-0 bg-gradient-to-r ${service.gradient} p-2 sm:p-4 rounded-lg sm:rounded-2xl shadow-lg backdrop-blur-sm border border-white/20`}
                  whileHover={{ 
                    scale: 1.05,
                    rotate: -3,
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <div className="flex items-center space-x-2 sm:space-x-3 text-white">
                    <motion.span 
                      className="text-lg sm:text-2xl"
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: Math.random() * 3 }}
                    >
                      {service.icon}
                    </motion.span>
                    <span className="font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap">
                      {service.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Second diagonal marquee - moving left */}
        <div className="transform -rotate-1 sm:-rotate-3 overflow-hidden">
          <div className="relative py-2 sm:py-4">
            <div className="flex animate-marquee-left gap-3 sm:gap-6 whitespace-nowrap">
              {[...extendedServices].reverse().map((service, index) => (
                <motion.div
                  key={`bottom-${index}`}
                  className={`flex-shrink-0 bg-gradient-to-r ${service.gradient} p-2 sm:p-4 rounded-lg sm:rounded-2xl shadow-lg backdrop-blur-sm border border-white/20`}
                  whileHover={{ 
                    scale: 1.05,
                    rotate: 3,
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <div className="flex items-center space-x-2 sm:space-x-3 text-white">
                    <motion.span 
                      className="text-lg sm:text-2xl"
                      animate={{ rotate: [0, -10, 10, 0] }}
                      transition={{ duration: 2.5, repeat: Infinity, repeatDelay: Math.random() * 3 }}
                    >
                      {service.icon}
                    </motion.span>
                    <span className="font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap">
                      {service.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Call to action */}
      <motion.div
        className="text-center mt-8 sm:mt-12 relative z-10"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        viewport={{ once: true }}
      >
        <motion.button
          className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-4 sm:px-8 sm:py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-sm sm:text-base"
          whileHover={{ 
            scale: 1.05,
            boxShadow: "0 20px 25px -5px rgba(59, 130, 246, 0.3), 0 10px 10px -5px rgba(168, 85, 247, 0.2)"
          }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            navigate('/services');
            // Scroll to services section after navigation
            setTimeout(() => {
              const servicesSection = document.querySelector('.services-title');
              if (servicesSection) {
                servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 300);
          }}
        >
          <span className="flex items-center space-x-1 sm:space-x-2">
            <span>Explore All Services</span>
            <motion.span
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              →
            </motion.span>
          </span>
        </motion.button>
      </motion.div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-primary-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default ServicesMarquee;
